// Shared "discard unsaved changes?" guard for edit/create dialogs.
//
// Usage:
//   const close = () => setOpen(false);            // the real close
//   const { attemptClose, discardDialog } = useUnsavedGuard({
//     open, value: form, onClose: close,           // value = whatever you edit
//   });
//   <Dialog open={open} onClose={attemptClose}> … <Button onClick={attemptClose}>Cancel</Button> …
//   {discardDialog}
//
// `value` is any serialisable snapshot of the form (an object, or a composite
// like { form, lines }). The hook baselines it each time the dialog opens and
// treats the form as dirty when it differs. Confirmation/viewer dialogs should
// NOT use this — only dialogs that hold editable data.
//
// Two baselining modes:
//   • Auto (default) — snapshots `value` on the open transition. Use when the
//     form is populated synchronously before opening (the common list pattern:
//     setForm({…}); setOpen(true)).
//   • Explicit — pass `initialValue` and the hook compares against that instead.
//     Use when the form is filled in a useEffect on open, so `value` isn't
//     settled on the first open render.
import { useState, useRef, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
} from '@mui/material';

const serialize = (v) => (typeof v === 'string' ? v : JSON.stringify(v ?? null));

export function useUnsavedGuard({ open, value, initialValue, onClose, message }) {
  const explicit = initialValue !== undefined;
  const baseline = useRef(serialize(value));
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Re-baseline whenever the dialog (re)opens, so each session starts clean.
  useEffect(() => {
    if (open) { baseline.current = serialize(value); setConfirmOpen(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isDirty = () =>
    serialize(value) !== (explicit ? serialize(initialValue) : baseline.current);

  // Route backdrop / Escape / Cancel through this. Prompts when dirty.
  const attemptClose = () => {
    if (isDirty()) setConfirmOpen(true);
    else onClose();
  };

  // Call after a successful save if you keep the dialog open — re-baselines
  // so the just-saved state no longer counts as unsaved.
  const markSaved = () => { baseline.current = serialize(value); };

  const discardDialog = (
    <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
      <DialogTitle>Discard unsaved changes?</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          {message || 'You have unsaved changes. Close without saving?'}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmOpen(false)}>Keep editing</Button>
        <Button color="error" variant="contained"
          onClick={() => { setConfirmOpen(false); onClose(); }}>
          Discard changes
        </Button>
      </DialogActions>
    </Dialog>
  );

  return { attemptClose, discardDialog, isDirty, markSaved };
}

export default useUnsavedGuard;
